/* Product cards that behave like the homepage wall, plus a carousel for "You may also like".
 *
 * Omar, 2026-09-05: "I don't want the price showing. I want it just like the wall where if you
 * hover over the product image, the price shows along with the add to cart button. Also, what if
 * we do a carousel so instead of limiting to four products it just keeps going."
 *
 * WHY JAVASCRIPT MOVES THE NODES INSTEAD OF LIQUID RENDERING THEM THERE.
 * Dawn's card puts the image in .card__inner and the title, price and quick-add in a sibling
 * .card__content underneath. To overlay price and button ON the image the way the wall does, they
 * have to live inside .card__inner, and CSS cannot reparent. The alternative was editing
 * card-product.liquid, which carries an explicit note that its BENHALEX GUARD was written as a
 * one-line change specifically so a future Dawn upgrade diffs cleanly; restructuring it would
 * throw that away. Moving two nodes at runtime is the smaller price.
 *
 * ⚠️ IT MOVES THE EXISTING NODES, IT DOES NOT REBUILD THEM. The price element and the quick-add
 * form are Dawn's own, already rendered through bhx-gate: a viewer who may not see prices gets
 * NEITHER in the HTML, so there is nothing for this to reveal. If it finds no quick-add it leaves
 * the card alone entirely, which is exactly the gated case.
 *
 * ⚠️ PROGRESSIVE. With this script absent the cards render as Dawn intended, price visible under
 * the title and the add button in flow. Nothing here is load-bearing for buying.
 */
(function () {
  "use strict";

  /* ---------- 1. Wall-style hover overlay ---------- */

  function overlayCard(card) {
    if (card.dataset.bhxCard === "1") return;
    var inner = card.querySelector(".card__inner");
    var quickAdd = card.querySelector(".quick-add");
    // No quick-add means the viewer is gated, or the product is not orderable. Leave it be:
    // the price slot in the card already shows the login or price-list route.
    if (!inner || !quickAdd) return;
    card.dataset.bhxCard = "1";

    var price = card.querySelector(".card-information .price, .card__information .price");

    var overlay = document.createElement("div");
    overlay.className = "bhx-card-hover";
    if (price) overlay.appendChild(price);
    overlay.appendChild(quickAdd);
    inner.appendChild(overlay);
    card.classList.add("bhx-card--overlay");
  }

  /* ---------- 2. Carousel ---------- */

  function arrow(dir) {
    var b = document.createElement("button");
    b.type = "button";
    b.className = "bhx-carousel__arrow bhx-carousel__arrow--" + dir;
    b.setAttribute("aria-label", dir === "prev" ? "Previous products" : "Next products");
    b.innerHTML =
      '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" ' +
      'stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
      (dir === "prev" ? '<path d="M15 18l-6-6 6-6"/>' : '<path d="M9 6l6 6-6 6"/>') +
      "</svg>";
    return b;
  }

  function carousel(list) {
    if (list.dataset.bhxCarousel === "1") return;
    var items = list.children.length;
    // Nothing to scroll past. A carousel with no overflow is just a grid wearing arrows.
    if (items < 2) return;
    list.dataset.bhxCarousel = "1";
    list.classList.add("bhx-carousel__track");

    var shell = document.createElement("div");
    shell.className = "bhx-carousel";
    list.parentNode.insertBefore(shell, list);
    shell.appendChild(list);

    var prev = arrow("prev");
    var next = arrow("next");
    shell.appendChild(prev);
    shell.appendChild(next);

    function step(direction) {
      var first = list.querySelector("li");
      // Scroll by one card plus the gap, so the row always lands on a card edge rather than
      // halfway through one.
      var width = first ? first.getBoundingClientRect().width + 16 : 300;
      list.scrollBy({ left: direction * width * 2, behavior: "smooth" });
    }
    prev.addEventListener("click", function () { step(-1); });
    next.addEventListener("click", function () { step(1); });

    function syncArrows() {
      var max = list.scrollWidth - list.clientWidth;
      // 2px of slack: sub-pixel layout means scrollLeft rarely hits max exactly.
      var atStart = list.scrollLeft <= 2;
      var atEnd = list.scrollLeft >= max - 2;
      prev.hidden = atStart;
      next.hidden = atEnd;
      // No overflow at all: hide both rather than showing two dead controls.
      if (max <= 2) { prev.hidden = true; next.hidden = true; }
      // The same two facts drive the edge fades, so a side only fades when something is
      // actually hidden behind it. See the mask rules in bhx.css.
      shell.classList.toggle("is-start", atStart || max <= 2);
      shell.classList.toggle("is-end", atEnd || max <= 2);
    }
    list.addEventListener("scroll", syncArrows, { passive: true });
    window.addEventListener("resize", syncArrows);
    if (window.ResizeObserver) new ResizeObserver(syncArrows).observe(list);
    syncArrows();

    /* ---------- Auto-scroll: CONTINUOUS, like the wall ----------
       Omar, 2026-09-05: "I want it to automatically be scrolling from left to right just like the
       wall, and at the end I just want it to restart."

       ⚠️ THE FIRST VERSION WAS WRONG AND THIS REPLACES IT. It advanced one card every four
       seconds on a setInterval, which is a slideshow, not a scroll: the row sat still most of the
       time and then jumped. This moves the row by a fraction of a pixel every animation frame, so
       it drifts the way the homepage wall does.

       ⚠️ requestAnimationFrame, NOT setInterval. A timer runs at a fixed rate regardless of the
       display, so the same code crawls on a 144Hz screen and stutters on a busy tab. rAF is tied
       to the actual frame, and the step is scaled by elapsed time so the SPEED is the same
       everywhere even when frames are dropped.

       ⚠️ SCROLL SNAP HAD TO GO. `scroll-snap-type: x mandatory` fights a sub-pixel programmatic
       scroll: the browser keeps pulling the row back to the nearest card edge, so it judders and
       never actually advances. Snap is off while auto-scrolling; the arrows still land on a card
       edge because they scroll by a card's width.

       At the end it restarts, per Omar. That is a visible jump back rather than the wall's
       seamless clone, and it is what he asked for; the wall needs seamlessness because it never
       stops, this row is a browsing aid people look away from. */
    var reduced = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (!reduced) {
      var PX_PER_SECOND = 28;   // Slow enough to read a card as it passes.
      var raf = null;
      var last = null;
      var resume = null;
      var carry = 0;            // Sub-pixel remainder; scrollLeft is whole pixels in most engines.

      function frame(now) {
        if (last === null) last = now;
        var dt = Math.min((now - last) / 1000, 0.1);
        last = now;

        var max = list.scrollWidth - list.clientWidth;
        if (max > 2) {
          if (list.scrollLeft >= max - 1) {
            list.scrollLeft = 0;
            carry = 0;
          } else {
            carry += PX_PER_SECOND * dt;
            var whole = Math.floor(carry);
            if (whole >= 1) {
              list.scrollLeft = list.scrollLeft + whole;
              carry -= whole;
            }
          }
        }
        raf = requestAnimationFrame(frame);
      }

      function start() {
        if (raf) return;
        last = null;
        list.style.scrollSnapType = "none";
        raf = requestAnimationFrame(frame);
      }
      function stop() {
        if (raf) { cancelAnimationFrame(raf); raf = null; }
        if (resume) { clearTimeout(resume); resume = null; }
      }
      function pauseThenResume() { stop(); resume = setTimeout(start, 4000); }

      // It yields to the person: reading a card should not have it slide out from under them,
      // and without this the drift fights the hover overlay on every card.
      shell.addEventListener("mouseenter", stop);
      shell.addEventListener("mouseleave", pauseThenResume);
      shell.addEventListener("focusin", stop);
      shell.addEventListener("focusout", pauseThenResume);
      shell.addEventListener("touchstart", pauseThenResume, { passive: true });
      list.addEventListener("wheel", pauseThenResume, { passive: true });
      prev.addEventListener("click", pauseThenResume);
      next.addEventListener("click", pauseThenResume);
      document.addEventListener("visibilitychange", function () {
        if (document.visibilityState === "visible") start(); else stop();
      });

      start();
    }
  }

  function boot(root) {
    var scope = root || document;
    scope.querySelectorAll(".card").forEach(overlayCard);
    scope.querySelectorAll(
      ".product-recommendations .grid, .related-products .grid, .bhx-recent__list"
    ).forEach(carousel);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () { boot(); });
  } else {
    boot();
  }
  // Recommendations arrive via a fetch after first paint, so re-run when they land.
  document.addEventListener("shopify:section:load", function (e) { boot(e.target); });
  // Recently viewed injects its cards after this script has already run, so it announces them.
  document.addEventListener("bhx:cards-added", function (e) {
    boot(e.detail && e.detail.root ? e.detail.root : document);
  });
  var recs = document.querySelector("product-recommendations");
  if (recs && window.MutationObserver) {
    new MutationObserver(function () { boot(recs); }).observe(recs, { childList: true, subtree: true });
  }
})();
